<template>
  <div class="records-stats">
    <!-- Overall statistics -->
    <div class="stats-overview">
      <div class="stat-item">
        <div class="stat-number">{{ stats.total }}</div>
        <div class="stat-label">總記錄</div>
      </div>
      
      <div class="stat-item success">
        <div class="stat-number">{{ stats.successful }}</div>
        <div class="stat-label">成功發現</div>
      </div>
      
      <div class="stat-item missing" v-if="stats.missing > 0">
        <div class="stat-number">{{ stats.missing }}</div>
        <div class="stat-label">搜尋失敗</div>
      </div>
      
      <div class="stat-item rate">
        <div class="stat-number">{{ stats.successRate }}%</div>
        <div class="stat-label">成功率</div>
      </div>
    </div>
    
    <!-- Success rate progress bar -->
    <div class="progress-section" v-if="stats.total > 0">
      <div class="progress-label">發現進度</div>
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          :style="{ width: `${stats.successRate}%` }"
        ></div>
      </div>
    </div>
    
    <!-- Type breakdown -->
    <div class="type-breakdown" v-if="hasTypeStats">
      <div class="breakdown-title">分類統計</div>
      <div class="type-stats">
        <div 
          v-for="(typeStat, type) in stats.byType" 
          :key="type"
          class="type-stat-item"
          v-show="typeStat.count > 0"
        >
          <img 
            v-if="typeStat.icon" 
            :src="typeStat.icon" 
            :alt="typeStat.label"
            class="type-icon"
          />
          <span class="type-label">{{ typeStat.label }}</span>
          <span class="type-count">{{ typeStat.count }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Records Statistics component following SOLID principles
 * Single Responsibility: Display discovery statistics and progress
 * Interface Segregation: Only receives the stats it needs
 */
export default {
  name: 'RecordsStats',
  
  props: {
    stats: {
      type: Object,
      required: true,
      default: () => ({
        total: 0,
        successful: 0,
        missing: 0,
        successRate: 0,
        byType: {}
      })
    }
  },
  
  computed: {
    /**
     * Check if there are any type statistics to display
     */
    hasTypeStats() {
      return Object.values(this.stats.byType).some(stat => stat.count > 0);
    }
  }
};
</script>

<style lang="scss" scoped>
.records-stats {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  padding: 0.5rem;
  margin-bottom: 0.5rem;

  .stats-overview {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.3rem;
    margin-bottom: 0.3rem;

    .stat-item {
      text-align: center;
      padding: 0.25rem;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.05);

      &.success {
        background: rgba(40, 167, 69, 0.1);
        border: 1px solid rgba(40, 167, 69, 0.2);

        .stat-number {
          color: #28a745;
        }
      }

      &.missing {
        background: rgba(220, 53, 69, 0.1);
        border: 1px solid rgba(220, 53, 69, 0.2);

        .stat-number {
          color: #dc3545;
        }
      }

      &.rate {
        background: rgba(0, 123, 255, 0.1);
        border: 1px solid rgba(0, 123, 255, 0.2);

        .stat-number {
          color: #007bff;
        }
      }

      .stat-number {
        font-size: 1rem;
        font-weight: 700;
        margin-bottom: 0.1rem;
        color: #333;
      }

      .stat-label {
        font-size: 0.65rem;
        color: #666;
        font-weight: 500;
      }
    }
  }

  .progress-section {
    margin-bottom: 0.5rem;

    .progress-label {
      font-size: 0.7rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 0.25rem;
    }

    .progress-bar {
      width: 100%;
      height: 5px;
      background-color: #e9ecef;
      border-radius: 3px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #28a745 0%, #20c997 100%);
        border-radius: 3px;
        transition: width 0.5s ease;
      }
    }
  }

  .type-breakdown {
    .breakdown-title {
      font-size: 0.7rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 0.25rem;
    }

    .type-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
      gap: 0.3rem;

      .type-stat-item {
        display: flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.25rem 0.4rem;
        background: rgba(0, 0, 0, 0.05);
        border-radius: 3px;

        .type-icon {
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }

        .type-label {
          flex: 1;
          font-size: 0.7rem;
          color: #555;
        }

        .type-count {
          font-weight: 600;
          color: #333;
          font-size: 0.75rem;
        }
      }
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .records-stats {
    .stats-overview {
      grid-template-columns: repeat(4, 1fr);
      gap: 0.3rem;

      .stat-item {
        padding: 0.2rem;

        .stat-number {
          font-size: 0.9rem;
        }

        .stat-label {
          font-size: 0.6rem;
        }
      }
    }

    .type-breakdown {
      .type-stats {
        grid-template-columns: repeat(2, 1fr);
        gap: 0.2rem;

        .type-stat-item {
          padding: 0.2rem 0.3rem;
        }
      }
    }
  }
}

// Animation for stats appearing
.records-stats {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>